import { useState } from 'react';
import { BookOpen, CheckCircle2, ChevronRight } from 'lucide-react';

// Moje vzdělání v Osobním rozvoji (mobil i desktop). Jediné místo, kde
// zaměstnanec vidí svá školení — modul Vzdělávání se 2026-09-28 stal HR
// záložkou Lidí. Tady vidí, co má rozpracované nebo přiřazené a co absolvoval.
// Data jsou průběhy z /api/moje-skoleni (načítá je Přehled).

const FIALOVA = '#B47AEA';
const STAV = {
    prirazeno: { label: 'K absolvování', color: 'var(--text-secondary)' },
    zahajeno:  { label: 'Rozpracováno', color: '#fbbf24' },
    neuspesne: { label: 'Neúspěšné — zkus znovu', color: '#f87171' },
};
const NAHLED_ABSOLVOVANYCH = 3;

const datum = (d) => d ? new Date(d).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }) : null;
const nejlepsiKviz = (p) => (p.kviz_vysledky || []).filter(k => k.uspel).reduce((max, k) => Math.max(max, Number(k.uspesnost_pct) || 0), -1);

export default function MojeVzdelani({ skoleni, isDark, onNavigate }) {
    const [vse, setVse] = useState(false);
    const otevrene = (skoleni || []).filter(p => p.stav !== 'dokonceno');
    const absolvovane = (skoleni || []).filter(p => p.stav === 'dokonceno')
        .sort((a, b) => new Date(b.dokonceno_at || 0) - new Date(a.dokonceno_at || 0));
    if (otevrene.length === 0 && absolvovane.length === 0) return null;

    const plocha = isDark ? 'rgba(255,255,255,0.055)' : 'var(--surface-raised)';
    const okraj = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const linka = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const nadpis = { color: 'var(--text-secondary)', letterSpacing: '0.08em', fontWeight: 600 };
    const otevrit = (p) => onNavigate?.('moje-skoleni-detail', { skoleniId: p.id });
    const zobrazene = vse ? absolvovane : absolvovane.slice(0, NAHLED_ABSOLVOVANYCH);

    return (
        <div style={{ marginBottom: 20 }}>
            {otevrene.length > 0 && <>
                <p className="text-[10px] uppercase mb-2" style={{ ...nadpis, color: 'var(--brand-text)' }}>Moje vzdělání · k absolvování</p>
                <div className="space-y-2 mb-4">
                    {otevrene.map((p, i) => {
                        const stav = STAV[p.stav] || STAV.prirazeno;
                        const termin = datum(p.prirazeni?.termin_do);
                        const hlavni = i === 0;
                        return (
                            <button key={p.id} onClick={() => otevrit(p)} className="w-full text-left rounded-xl"
                                style={{ background: plocha, border: `1px solid ${hlavni ? 'var(--brand)' : okraj}`, padding: 12, cursor: 'pointer', display: 'block' }}>
                                <div className="flex items-center gap-3">
                                    <div className="shrink-0 flex items-center justify-center" style={{ width: 38, height: 38, borderRadius: 11, background: `${FIALOVA}1f`, color: FIALOVA }}>
                                        <BookOpen size={18} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{p.skoleni?.nazev}</div>
                                        <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                            <span style={{ color: stav.color }}>{stav.label}</span>
                                            {termin && <> · termín {termin}</>}
                                            {!termin && p.skoleni?.sekce?.length > 0 && <> · {p.skoleni.sekce.length} sekcí</>}
                                        </div>
                                    </div>
                                    {!hlavni && <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />}
                                </div>
                                {hlavni && (
                                    <div className="flex items-center gap-3 mt-3">
                                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: linka }}>
                                            <div className="h-full" style={{ width: `${p.progress_pct || 0}%`, background: 'linear-gradient(90deg, var(--brand), var(--brand-purple))' }} />
                                        </div>
                                        <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: 'var(--brand)', color: '#1a1000' }}>
                                            {p.stav === 'prirazeno' ? 'Začít' : 'Pokračovat'}
                                        </span>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </>}

            {absolvovane.length > 0 && <>
                <p className="text-[10px] uppercase mb-2" style={nadpis}>Absolvovaná ({absolvovane.length})</p>
                <div className="rounded-xl" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-raised)', border: `1px solid ${okraj}`, padding: '2px 12px' }}>
                    {zobrazene.map((p, i) => {
                        const kviz = nejlepsiKviz(p);
                        return (
                            <button key={p.id} onClick={() => otevrit(p)} className="w-full flex items-center gap-2.5 text-left"
                                style={{ padding: '9px 0', borderTop: i ? `1px solid ${linka}` : 'none', background: 'none', cursor: 'pointer' }}>
                                <CheckCircle2 size={18} style={{ color: '#10b981', flexShrink: 0 }} />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{p.skoleni?.nazev}</div>
                                    <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                                        {datum(p.dokonceno_at) || 'Dokončeno'}
                                        {kviz >= 0 && <> · kvíz {Math.round(kviz)} %</>}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                    {absolvovane.length > NAHLED_ABSOLVOVANYCH && (
                        <button onClick={() => setVse(v => !v)} className="text-xs font-semibold"
                            style={{ color: 'var(--brand-text)', padding: '4px 0 10px 28px', background: 'none', cursor: 'pointer' }}>
                            {vse ? 'Zobrazit méně' : `Zobrazit všech ${absolvovane.length}`}
                        </button>
                    )}
                </div>
            </>}
        </div>
    );
}
